import React, { useEffect, useState } from "react";
import { LogoLockup } from "./Logo";
import LanguageSelector from "./LanguageSelector";
import MagneticButton from "./MagneticButton";
import { useI18n } from "../i18n/I18nProvider";
import { NAV } from "../constants/testIds";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { openWhatsapp } from "../lib/whatsapp";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Navbar = () => {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const items = [
    { id: "capabilities", label: t.nav.capabilities, testId: NAV.linkCapabilities },
    { id: "technology", label: t.nav.technology, testId: NAV.linkTechnology },
    { id: "showcase", label: t.nav.showcase, testId: NAV.linkShowcase },
    { id: "philosophy", label: t.nav.philosophy, testId: NAV.linkPhilosophy },
    { id: "contact", label: t.nav.contact, testId: NAV.linkContact },
  ];

  return (
    <header
      data-testid={NAV.root}
      className={
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500 " +
        (scrolled
          ? "backdrop-blur-2xl bg-black/70 border-b border-white/10"
          : "bg-transparent border-b border-transparent")
      }
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 flex items-center justify-between h-[72px]">
        <a
          href="#top"
          data-testid={NAV.logo}
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center"
          aria-label="GAELON"
        >
          <LogoLockup height={34} />
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {items.map((it) => (
            <button
              key={it.id}
              data-testid={it.testId}
              onClick={() => scrollToId(it.id)}
              className="text-[11px] tracking-[0.22em] uppercase text-white/65 hover:text-white transition-colors duration-300"
            >
              {it.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSelector scope="nav" />
          <div className="hidden md:block">
            <MagneticButton
              variant="primary"
              data-testid={NAV.ctaBookCall}
              onClick={() => openWhatsapp(t.locale)}
              className="px-5 py-3 text-[11px]"
            >
              {t.nav.bookCall}
            </MagneticButton>
          </div>
          <button
            data-testid={NAV.mobileMenuToggle}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-white/15 text-white"
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileMenu}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden bg-black border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {items.map((it) => (
                <button
                  key={it.id}
                  data-testid={`${it.testId}-mobile`}
                  onClick={() => {
                    setOpen(false);
                    scrollToId(it.id);
                  }}
                  className="text-left text-[13px] tracking-[0.22em] uppercase text-white/75 hover:text-white py-2"
                >
                  {it.label}
                </button>
              ))}
              <MagneticButton
                variant="primary"
                data-testid={`${NAV.ctaBookCall}-mobile`}
                onClick={() => {
                  setOpen(false);
                  openWhatsapp(t.locale);
                }}
                className="mt-2 self-start"
              >
                {t.nav.bookCall}
              </MagneticButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
